import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import { useBiodataManagement } from "hooks/useBiodataManagement";
import { useNotifications } from "hooks/useNotifications";
import Notifier from "components/ui/Notifier";

import Icon from "components/AppIcon";

import BulkUploadModal from "./components/BulkUploadModal";
import BiodataTable from "./components/BiodataTable";
import DocumentUploadModal from "./components/DocumentUploadModal";
import BiodataDetailsView from "components/BiodataDetailsView";
import { biodataAPI } from "utils/api";
import { useBiodataCache } from "context/BiodataCacheContext";

const RecruiterBiodataManagement = () => {
  const navigate = useNavigate();
  const {
    notifications,
    showSuccess,
    showError,
    removeNotification: dismissNotification,
  } = useNotifications();
  const {
    getBiodataByStatus,
    getAllBiodatas,
    invalidateOnUpdate,
    getPhotoUrl,
    prefetchPhotoUrls,
    prefetchPhotoUrl,
  } = useBiodataCache();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("drafts");

  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showBiodataDetails, setShowBiodataDetails] = useState(false);
  const [selectedBiodata, setSelectedBiodata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [exportScope, setExportScope] = useState("all");
  // Delete confirmation modal state
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [biodataData, setBiodataData] = useState({
    drafts: [],
    submitted: [],
    approved: [],
    rejected: [],
    selected: [],
    resubmitted: [],
  });
  const [statusCounts, setStatusCounts] = useState({
    drafts: 0,
    submitted: 0,
    approved: 0,
    rejected: 0,
    selected: 0,
    resubmitted: 0,
  });

  const { exportBiodata, exportStatus } = useBiodataManagement({
    apiEndpoints: {
      list: "/biodatas",
      export: "/biodatas/export",
      print: "/biodatas/print",
    },
    biodataType: activeTab,
    enableDataFetching: false,
  });

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms = 0.5 seconds delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Load status counts on component mount
  useEffect(() => {
    const loadStatusCounts = async () => {
      try {
        const response = await biodataAPI.getStatusCounts();
        if (response.status === "success") {
          setStatusCounts(response.data);
        }
      } catch (err) {
        console.error("Failed to load status counts:", err);
      }
    };
    loadStatusCounts();
  }, []);

  // Function to refresh status counts
  const refreshStatusCounts = async () => {
    try {
      const response = await biodataAPI.getStatusCounts();
      if (response.status === "success") {
        setStatusCounts(response.data);
      }
    } catch (err) {
      console.error("Failed to refresh status counts:", err);
    }
  };

  // Fetch biodata from API when activeTab, currentPage, pageSize, searchTerm, filterType, or sort changes
  useEffect(() => {
    const fetchBiodata = async () => {
      setLoading(true);
      setError("");
      try {
        // Map tab to status for API
        const statusMap = {
          drafts: "draft",
          submitted: "pending",
          approved: "approved",
          rejected: "rejected",
          selected: "selected",
          resubmitted: "resubmitted",
        };
        const status = statusMap[activeTab];
        let params = {
          per_page: pageSize,
          page: currentPage,
          sort_by: sortBy,
          sort_order: sortOrder,
        };
        if (debouncedSearchTerm) params.search = debouncedSearchTerm;
        if (filterType !== "all") params.type = filterType;
        let response;
        if (status) {
          response = await getBiodataByStatus(status, params);
        } else {
          response = await getAllBiodatas(params);
        }

        if (response.fromCache) {
        }

        if (response.status !== "success") {
          throw new Error(response.message || "Failed to load biodata");
        }

        const items = Array.isArray(response.records)
          ? response.records
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const pagination =
          response.pagination || response.data?.pagination || {};
        const totalCount =
          typeof pagination.total === "number"
            ? pagination.total
            : items.length;
        const totalPages = pagination.total_pages
          ? pagination.total_pages
          : totalCount > 0
          ? Math.ceil(totalCount / pageSize)
          : 0;

        setTotalItems(totalCount);
        if (totalPages > 0 && currentPage > totalPages) {
          setCurrentPage(totalPages);
        }
        // Map API fields to table fields
        const mapped = items.map((item) => ({
          id: item.id,
          name: item.full_name || item.name,
          passport: item.passport_no || item.passport,
          type: item.vdr_type || item.type,
          country: item.nationality || item.country,
          medical_expiry_date: item.medical_expiry_date,
          skill: item.skills || item.skill || "",
          created_date: item.created_at,
          last_modified: item.updated_at,
          submitted_date: item.submitted_at,
          approved_date: item.approved_at,
          rejected_date: item.latest_rejection?.rejected_at || item.rejected_at,
          selected_date: item.selected_at,
          resubmitted_date: item.resubmitted_at,
          expires_on: item.expires_on,
          status: item.status,
          clab_review: item.clab_review,
          rejection_reason:
            item.latest_rejection?.rejection_reason || item.rejection_reason,
          rejection_remarks:
            item.latest_rejection?.remarks || item.rejection_remarks || "",
          can_amend: item.can_amend,
          edit_permission_granted: item.edit_permission_granted,
          is_editable: item.is_editable,
          employer: item.employer,
          completion: item.completion || 0,
          days_remaining: item.days_remaining,
          photo_url: getPhotoUrl(item.id, item),
        }));
        setBiodataData((prev) => ({ ...prev, [activeTab]: mapped }));
        const ids = items.map((i) => i.id);
        if (ids.length) prefetchPhotoUrls(ids);
      } catch (err) {
        setError("Failed to load biodata.");
      } finally {
        setLoading(false);
      }
    };
    fetchBiodata();
  }, [
    activeTab,
    currentPage,
    pageSize,
    debouncedSearchTerm,
    filterType,
    sortBy,
    sortOrder,
    refreshTrigger,
  ]);

  // Progressive photo prefetch when rows become visible
  useEffect(() => {
    const elements = document.querySelectorAll("[data-biodata-id]");
    if (!elements || elements.length === 0) return;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idAttr = entry.target.getAttribute("data-biodata-id");
          const id = Number(idAttr) || idAttr;
          if (!id || seen.has(id)) return;
          // Try to prefetch only if not already resolved
          const list = biodataData[activeTab] || [];
          const rec = list.find((i) => i.id === id);
          const direct = rec?.photo_url;
          const cached = getPhotoUrl(id, rec);
          if (!direct && !cached) prefetchPhotoUrl(id);
          seen.add(id);
          observer.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.25 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeTab, biodataData, getPhotoUrl, prefetchPhotoUrl]);

  const tabs = [
    {
      id: "drafts",
      label: "Drafts",
      count: statusCounts.drafts,
      icon: "FileEdit",
    },
    {
      id: "submitted",
      label: "Pending",
      count: statusCounts.submitted,
      icon: "Send",
    },
    {
      id: "approved",
      label: "Approved",
      count: statusCounts.approved,
      icon: "CheckCircle",
    },
    {
      id: "rejected",
      label: "Rejected",
      count: statusCounts.rejected,
      icon: "XCircle",
    },
    {
      id: "resubmitted",
      label: "Resubmitted",
      count: statusCounts.resubmitted,
      icon: "RotateCcw",
    },
    {
      id: "selected",
      label: "Selected",
      count: statusCounts.selected,
      icon: "UserCheck",
    },
  ];

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleCreateBiodata = () => {
    navigate("/recruiter-biodata-edit");
  };

  const handleEditBiodata = (biodata) => {
    navigate(`/recruiter-biodata-edit/${biodata.id}`);
  };

  const handleAmendBiodata = (biodata) => {
    navigate(`/recruiter-biodata-edit/${biodata.id}?amend=1`);
  };

  const handleResubmitBiodata = async (biodata) => {
    try {
      const response = await biodataAPI.resubmitBiodata(biodata.id);
      invalidateOnUpdate([biodata.id], "rejected", "pending");
      if (response.status === "success") {
        showSuccess("Biodata resubmitted successfully!");
        // Refresh the data
        setRefreshTrigger((prev) => prev + 1);
      } else {
        showError(response.message || "Failed to resubmit biodata");
      }
    } catch (error) {
      console.error("Error resubmitting biodata:", error);
      showError("Failed to resubmit biodata. Please try again.");
    }
  };

  const handleDeleteDraft = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const resp = await biodataAPI.deleteBiodata(pendingDelete.id);
      invalidateOnUpdate([pendingDelete.id], "draft", "draft");
      if (resp.status === "success") {
        showSuccess(resp.message || "Draft deleted successfully");
        await refreshStatusCounts();
        setRefreshTrigger((prev) => prev + 1);
      } else {
        showError(resp.message || "Failed to delete draft");
      }
    } catch (delErr) {
      console.error("Delete draft error:", delErr);
      showError("Delete failed. Please try again.");
    } finally {
      setDeleting(false);
      setShowConfirmDelete(false);
      setPendingDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleting(false);
    setShowConfirmDelete(false);
    setPendingDelete(null);
  };

  const handleExportData = async () => {
    try {
      const statusMap = {
        drafts: "draft",
        submitted: "pending",
        approved: "approved",
        rejected: "rejected",
        selected: "selected",
        resubmitted: "resubmitted",
      };

      if (exportScope === "current_page") {
        const dataOnPage = getFilteredData();
        if (dataOnPage.length === 0) {
          showError("No biodata on the current page to export");
          return;
        }
      }

      const pagination =
        exportScope === "current_page"
          ? { page: currentPage, perPage: pageSize }
          : undefined;

      await exportBiodata([], "excel", {
        statusFilter:
          exportScope === "all" ? "all" : statusMap[activeTab] || activeTab,
        includeDocuments: false,
        includePhotos: false,
        useBackend: true,
        exportScope,
        pagination,
      });
    } catch (error) {
      console.error("Export failed:", error);
      showError("Export failed: " + (error.message || "Unknown error"));
    }
  };

  const handlePrintBiodata = (biodata) => {
    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Worker Biodata - ${biodata.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .section { margin-bottom: 20px; }
            .label { font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .status-${biodata.status} { color: ${
      biodata.status === "approved"
        ? "green"
        : biodata.status === "rejected"
        ? "red"
        : "orange"
    }; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Foreign Worker Biodata</h1>
            <p>eBiodata CLAB System - ${new Date().toLocaleDateString()}</p>
          </div>
          <table>
            <tr><th>Worker ID</th><td>${biodata.id}</td></tr>
            <tr><th>Full Name</th><td>${biodata.name}</td></tr>
            <tr><th>Passport Number</th><td>${biodata.passport}</td></tr>
            <tr><th>Biodata Type</th><td>${biodata.type}</td></tr>
            <tr><th>Nationality</th><td>${biodata.country}</td></tr>
            <tr><th>Medical Expiry</th><td>${
              biodata.medical_expiry_date
                ? new Date(biodata.medical_expiry_date).toLocaleDateString()
                : "Not set"
            }</td></tr>
            <tr><th>Status</th><td class="status-${
              biodata.status
            }">${biodata.status.toUpperCase()}</td></tr>
            ${
              biodata.created_date
                ? `<tr><th>Created Date</th><td>${new Date(
                    biodata.created_date
                  ).toLocaleDateString()}</td></tr>`
                : ""
            }
            ${
              biodata.submitted_date
                ? `<tr><th>Submitted Date</th><td>${new Date(
                    biodata.submitted_date
                  ).toLocaleDateString()}</td></tr>`
                : ""
            }
            ${
              biodata.approved_date
                ? `<tr><th>Approved Date</th><td>${new Date(
                    biodata.approved_date
                  ).toLocaleDateString()}</td></tr>`
                : ""
            }
            ${
              biodata.rejected_date
                ? `<tr><th>Rejected Date</th><td>${new Date(
                    biodata.rejected_date
                  ).toLocaleDateString()}</td></tr>`
                : ""
            }
            ${
              biodata.rejection_reason
                ? `<tr><th>Rejection Reason</th><td>${biodata.rejection_reason}</td></tr>`
                : ""
            }
            ${
              biodata.employer
                ? `<tr><th>Selected by Employer</th><td>${biodata.employer}</td></tr>`
                : ""
            }
          </table>
          <div class="section" style="margin-top: 30px;">
            <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            <p><strong>Recruiter:</strong> ${
              userProfile?.agency_name || "Unknown Agency"
            }</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadBiodata = (biodata) => {
    // Create CV/biodata download
    const biodataData = {
      workerInfo: {
        id: biodata.id,
        name: biodata.name,
        passport: biodata.passport,
        type: biodata.type,
        nationality: biodata.country,
        medical_expiry_date: biodata.medical_expiry_date,
        status: biodata.status,
      },
      dates: {
        created: biodata.created_date,
        submitted: biodata.submitted_date,
        approved: biodata.approved_date,
        rejected: biodata.rejected_date,
      },
      additionalInfo: {
        rejectionReason: biodata.rejection_reason,
        employer: biodata.employer,
        completion: biodata.completion,
      },
    };

    // Convert to JSON and download
    const dataStr = JSON.stringify(biodataData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `biodata_${biodata.name.replace(/\s+/g, "_")}_${
      biodata.id
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFilteredData = () => {
    let data = [...(biodataData[activeTab] || [])];

    // Apply search filter
    if (searchTerm) {
      data = data.filter(
        (item) =>
          (item.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.passport || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (item.country || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (item.medical_expiry_date || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== "all") {
      data = data.filter(
        (item) => (item.type || "").toLowerCase() === filterType.toLowerCase()
      );
    }

    // Apply sorting
    const dateFields = new Set([
      "created_date",
      "submitted_date",
      "approved_date",
      "rejected_date",
      "selected_date",
      "resubmitted_date",
      "medical_expiry_date",
    ]);
    data.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      if (dateFields.has(sortBy)) {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return data;
  };

  const getTabContent = () => {
    if (loading) {
      return <div className="text-center py-12">Loading biodata...</div>;
    }
    if (error) {
      return <div className="text-center py-12 text-error">{error}</div>;
    }
    const data = getFilteredData();

    if (data.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="FileText" size={32} className="text-secondary-400" />
          </div>
          <h3 className="text-lg font-medium text-text-primary mb-2">
            No biodata found
          </h3>
          <p className="text-text-secondary mb-6">
            {searchTerm || filterType !== "all"
              ? "No biodata matches your current filters."
              : `No ${activeTab} biodata available.`}
          </p>
          {activeTab === "drafts" && !searchTerm && filterType === "all" && (
            <button
              onClick={handleCreateBiodata}
              className="btn-primary px-6 py-2 rounded-lg font-medium"
            >
              Create Your First Biodata
            </button>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="flex items-center justify-end gap-2 mb-4">
          <span>Rows per page:</span>
          <select
            className="input-field"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            onClick={() => {
              refreshStatusCounts();
              setRefreshTrigger((prev) => prev + 1);
            }}
            className="p-2 rounded-md text-secondary-600 hover:bg-secondary-100 transition-colors duration-150"
            title="Refresh data"
          >
            <Icon name="RefreshCw" size={16} />
          </button>
        </div>
        <BiodataTable
          data={data}
          activeTab={activeTab}
          isDeleting={deleting}
          onEdit={handleEditBiodata}
          onAmend={handleAmendBiodata}
          onViewDocuments={(biodata) => {
            setSelectedBiodata(biodata);
            setShowDocumentUpload(true);
          }}
          onViewDetails={async (biodata) => {
            try {
              // Fetch complete biodata details including documents and photos
              const response = await biodataAPI.getBiodataById(biodata.id);
              if (response && response.status === "success" && response.data) {
                setSelectedBiodata(response.data);
              } else {
                // Fallback to the basic item if details fail
                setSelectedBiodata(biodata);
              }
            } catch (err) {
              console.error("Failed to load biodata details:", err);
              setSelectedBiodata(biodata);
            } finally {
              setShowBiodataDetails(true);
            }
          }}
          onPrint={handlePrintBiodata}
          onDownload={handleDownloadBiodata}
          onResubmit={handleResubmitBiodata}
          onDelete={(biodata) => {
            setPendingDelete(biodata);
            setShowConfirmDelete(true);
          }}
        />
        {/* Pagination Controls */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-secondary-600">
            Showing {data.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}-
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{" "}
            results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="btn-secondary px-3 py-1 rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span>Page {currentPage}</span>
            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={currentPage * pageSize >= totalItems}
              className="btn-secondary px-3 py-1 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <HeaderBar
        userRole="recruiter"
        onSidebarToggle={handleSidebarToggle}
        userName="Sarah Johnson"
      />

      {/* Sidebar */}
      <RoleBasedSidebar
        userRole="recruiter"
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      {/* Main Content */}
      <main
        className={`transition-all duration-300 ease-smooth ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        } pt-16`}
      >
        <div className="p-6">
          {/* Breadcrumb */}
          <BreadcrumbNavigation userRole="recruiter" />

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-text-primary mb-2">
                  Biodata Management
                </h1>
                <p className="text-text-secondary">
                  Create, manage, and track foreign worker biodata submissions
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowBulkUpload(true)}
                  className="btn-secondary px-4 py-2 rounded-lg font-medium flex items-center"
                >
                  <Icon name="Upload" size={18} className="mr-2" />
                  Bulk Upload
                </button>
                <div className="flex flex-col items-end space-y-2">
                  <div className="flex items-center space-x-2">
                    <select
                      value={exportScope}
                      onChange={(e) => setExportScope(e.target.value)}
                      className="px-3 py-2 text-sm border border-secondary-300 rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-primary-300"
                    >
                      <option value="all">All records (all statuses)</option>
                      <option value="filtered">Apply current filters</option>
                      <option value="current_page">Current page only</option>
                    </select>
                    <button
                      onClick={handleExportData}
                      disabled={exportStatus.isExporting}
                      className="btn-secondary px-4 py-2 rounded-lg font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon name="Download" size={18} className="mr-2" />
                      {exportStatus.isExporting ? "Exporting..." : "Export"}
                    </button>
                  </div>
                  {exportStatus.isExporting ||
                  exportStatus.stage === "downloading" ? (
                    <p className="text-xs text-text-secondary">
                      Exporting {exportStatus.totalRecords || "records"}
                      {exportStatus.estimatedSeconds
                        ? ` (~${Math.max(
                            1,
                            Math.round(exportStatus.estimatedSeconds)
                          )}s)`
                        : ""}{" "}
                      {exportStatus.downloadedBytes
                        ? `• ${
                            Math.round(
                              (exportStatus.downloadedBytes / 1024 / 1024) * 100
                            ) / 100
                          } MB downloaded`
                        : ""}
                    </p>
                  ) : exportStatus.stage === "completed" ? (
                    <p className="text-xs text-success-600">
                      Last export: {exportStatus.totalRecords || 0} records →{" "}
                      {exportStatus.filename || "download"}
                    </p>
                  ) : exportStatus.stage === "error" ? (
                    <p className="text-xs text-error-600">
                      Export failed: {exportStatus.error}
                    </p>
                  ) : null}
                </div>
                <button
                  onClick={handleCreateBiodata}
                  className="btn-primary px-4 py-2 rounded-lg font-medium flex items-center"
                >
                  <Icon name="Plus" size={18} className="mr-2" />
                  Create New Biodata
                </button>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {tabs.map((tab) => (
              <div key={tab.id} className="card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-text-secondary capitalize">
                      {tab.label}
                    </p>
                    <p className="text-2xl font-bold text-text-primary">
                      {tab.count}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Icon
                      name={tab.icon}
                      size={20}
                      className="text-primary-600"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs Navigation */}
          <div className="card mb-6">
            <div className="border-b border-secondary-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setCurrentPage(1);
                    }}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                      activeTab === tab.id
                        ? "border-primary text-primary"
                        : "border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name={tab.icon} size={16} />
                      <span>{tab.label}</span>
                      <span
                        className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          activeTab === tab.id
                            ? "bg-primary-100 text-primary-800"
                            : "bg-secondary-100 text-secondary-800"
                        }`}
                      >
                        {tab.count}
                      </span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>

            {/* Filters and Search */}
            <div className="p-6 border-b border-secondary-200">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search */}
                  <div className="relative">
                    <Icon
                      name="Search"
                      size={18}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400"
                    />
                    <input
                      type="text"
                      placeholder="Search biodata... (Press Enter or wait 0.5s)"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setDebouncedSearchTerm(searchTerm);
                        }
                      }}
                      className="input-field pl-10 pr-4 py-2 w-full sm:w-64"
                    />
                  </div>

                  {/* Type Filter */}
                  <select
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="input-field px-3 py-2"
                  >
                    <option value="all">All Types</option>
                    <option value="standard">Standard</option>
                    <option value="equotaspecial">eQuotaSpecial</option>
                  </select>
                </div>

                {/* Sort Options */}
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-text-secondary">
                    Sort by:
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="input-field px-3 py-2 text-sm"
                  >
                    <option value="created_date">Created Date</option>
                    <option value="name">Name</option>
                    <option value="country">Country</option>
                    <option value="medical_expiry_date">
                      Medical Expiry Date
                    </option>
                  </select>
                  <button
                    onClick={() => {
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      setCurrentPage(1);
                    }}
                    className="p-2 rounded-md text-secondary-600 hover:bg-secondary-100 transition-colors duration-150"
                  >
                    <Icon
                      name={sortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                      size={16}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">{getTabContent()}</div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {showBulkUpload && (
        <BulkUploadModal
          onClose={() => setShowBulkUpload(false)}
          onUpload={(uploadData) => {
            setShowBulkUpload(false);
            // Refresh status counts and reload current tab data
            refreshStatusCounts();
            setCurrentPage(1);
            setRefreshTrigger((prev) => prev + 1);
          }}
        />
      )}

      {showDocumentUpload && selectedBiodata && (
        <DocumentUploadModal
          biodata={selectedBiodata}
          onClose={() => {
            setShowDocumentUpload(false);
            setSelectedBiodata(null);
          }}
          onUpload={(documents) => {
            setShowDocumentUpload(false);
            setSelectedBiodata(null);
            // Refresh status counts and reload current tab data
            refreshStatusCounts();
            setCurrentPage(1);
            setRefreshTrigger((prev) => prev + 1);
          }}
        />
      )}

      {/* Biodata Details Overlay using shared viewer */}
      {showBiodataDetails && selectedBiodata && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="biodata-modal-overlay fixed inset-0 z-1001 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
            <div className="biodata-modal-content bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
                <h2 className="text-xl font-bold">Biodata Details</h2>
                <button
                  onClick={() => {
                    setShowBiodataDetails(false);
                    setSelectedBiodata(null);
                  }}
                  className="text-secondary-600 hover:text-secondary-900"
                >
                  Close
                </button>
              </div>
              <div className="p-6">
                <BiodataDetailsView biodata={selectedBiodata} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmDelete && pendingDelete && (
        <div className="fixed inset-0 z-1002 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-secondary-200">
              <h2 className="text-lg font-bold text-text-primary">
                Confirm Delete
              </h2>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-text-secondary">
                Are you sure you want to delete the draft biodata{" "}
                <strong>
                  {pendingDelete.name ||
                    pendingDelete.passport ||
                    `ID ${pendingDelete.id}`}
                </strong>
                ? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-end gap-3">
              <button
                className="btn-secondary px-4 py-2 rounded-lg"
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-error-600 text-white hover:bg-error-700 transition-colors duration-150 disabled:opacity-60"
                onClick={handleDeleteDraft}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Notifier for success/error toasts */}
      <Notifier
        type={
          notifications.length > 0
            ? notifications[notifications.length - 1].type
            : "info"
        }
        message={
          notifications.length > 0
            ? notifications[notifications.length - 1].message
            : ""
        }
        show={notifications.length > 0}
        onClose={() =>
          dismissNotification(notifications[notifications.length - 1]?.id)
        }
        duration={
          notifications.length > 0
            ? notifications[notifications.length - 1].duration
            : 3000
        }
      />
    </div>
  );
};

export default RecruiterBiodataManagement;
